import { z } from "zod";
import { CommitShaSchema, IdSchema, PrincipalSchema, ScopeSchema, TimestampSchema, WpIdSchema } from "./primitives.ts";

/**
 * Lifecycle of a work package.
 * - `draft`: in a plan nobody has approved yet.
 * - `approved`: the plan was approved; waiting to be dispatched.
 * - `dispatched`: a fork and brief exist; waiting for an agent session to register.
 * - `in_progress`: an agent session is working on it.
 * - `verifying`: the agent asked to land; waiting on a verdict or on landing gates.
 * - `landed`: merged into main.
 * - `failed`: the last verification failed. The agent fixes it and requests land again.
 * - `superseded` (contracts-v3): replaced by a plan amendment (`amendPlan`). It never lands; packages that depended
 *   on it depend on its replacements instead.
 */
export const WorkPackageStatusSchema = z.enum([
  "draft",
  "approved",
  "dispatched",
  "in_progress",
  "verifying",
  "landed",
  "failed",
  "superseded",
]);
/** See {@link WorkPackageStatusSchema}. */
export type WorkPackageStatus = z.infer<typeof WorkPackageStatusSchema>;

/** One checkable acceptance criterion. Written so a verifier can decide met or not met with evidence. */
export const AcceptanceCriterionSchema = z.object({
  /** Short id, unique within the package, for example `AC-1`. Verdicts reference it. */
  id: z.string().regex(/^[A-Za-z0-9][\w.-]{0,31}$/),
  /** The criterion, for example "POST /matches/:id/dispute returns 201 and stores the dispute". */
  text: z.string().min(1),
  /** How to check it: a test name, a command, a URL to hit, or what to look for in the diff. */
  check: z.string().min(1),
});
/** See {@link AcceptanceCriterionSchema}. */
export type AcceptanceCriterion = z.infer<typeof AcceptanceCriterionSchema>;

/**
 * Where a work package came from in its spec: one section or paragraph range of a markdown spec file.
 * Lets a reader (the dashboard, intent blame, a reviewer) go from a package back to the exact words it delivers,
 * and tell when the spec has changed since the package was planned.
 */
export const SpecAnchorSchema = z
  .object({
    /** Repo-relative path of the spec file, for example `demo/SPEC-season-2.md`. No leading `/` or `./`. */
    path: z
      .string()
      .min(1)
      .refine((s) => !s.startsWith("/") && !s.startsWith("./") && !s.includes("\\"), "repo-relative path with / separators"),
    /** Heading text of the section, for example "Match disputes". */
    heading: z.string().min(1),
    /** GitHub-style slug of the heading, for example `match-disputes`. Survives line shifts; link with `#<anchor>`. */
    anchor: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
    /** First line of the anchored text, 1-based, in the spec as planned. */
    startLine: z.number().int().min(1),
    /** Last line of the anchored text, inclusive. */
    endLine: z.number().int().min(1),
    /** Commit the spec file was read at. Null when the spec reached the planner without one (posted directly). */
    commit: CommitShaSchema.nullable(),
    /** Lowercase hex SHA-256 of the anchored lines joined with `\n`, so a reader can tell the text changed since planning. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .refine((a) => a.endLine >= a.startLine, { message: "endLine must not be before startLine", path: ["endLine"] });
/** See {@link SpecAnchorSchema}. */
export type SpecAnchor = z.infer<typeof SpecAnchorSchema>;

const workPackageFields = {
  /** The work package id, for example `WP-21`. Unique across the coordinator. */
  id: WpIdSchema,
  /** Short title, for example "Match disputes". */
  title: z.string().min(1).max(120),
  /** The part of the spec this package delivers, quoted or closely paraphrased. */
  specExcerpt: z.string().min(1),
  /** What the package is expected to touch. Same shape as a claim scope. */
  scope: ScopeSchema,
  /** What "done" means. At least one. */
  acceptanceCriteria: z.array(AcceptanceCriterionSchema).min(1),
  /** Packages that must land before this one can land. Must be in the same plan. */
  dependsOn: z.array(WpIdSchema),
  /**
   * Where `specExcerpt` sits in the spec: the paragraphs this package was cut from, in spec order.
   * Optional: plans made before anchors existed, or from a spec with no file, leave it out.
   */
  specAnchors: z.array(SpecAnchorSchema).optional(),
};

function criteriaIdsUnique(p: { acceptanceCriteria: { id: string }[] }): boolean {
  return new Set(p.acceptanceCriteria.map((c) => c.id)).size === p.acceptanceCriteria.length;
}

/** A work package as a planner submits it, before the coordinator tracks its status. */
export const WorkPackageDraftSchema = z
  .object(workPackageFields)
  .refine(criteriaIdsUnique, { message: "acceptance criterion ids must be unique", path: ["acceptanceCriteria"] })
  .refine((p) => !p.dependsOn.includes(p.id), { message: "a package cannot depend on itself", path: ["dependsOn"] });
/** See {@link WorkPackageDraftSchema}. */
export type WorkPackageDraft = z.infer<typeof WorkPackageDraftSchema>;

/** One unit of work for one agent: a slice of the spec with scope, criteria and dependencies. */
export const WorkPackageSchema = z
  .object({
    ...workPackageFields,
    /** Current lifecycle state, see {@link WorkPackageStatusSchema}. */
    status: WorkPackageStatusSchema,
    /** The fork created for this package at dispatch, as `<owner>/<repo>` or an Artifacts repo name. Null before dispatch. */
    forkRepo: z.string().min(1).nullable(),
    /** The session currently delivering it. Null until an agent registers for it. */
    sessionId: IdSchema.nullable(),
  })
  .refine(criteriaIdsUnique, { message: "acceptance criterion ids must be unique", path: ["acceptanceCriteria"] })
  .refine((p) => !p.dependsOn.includes(p.id), { message: "a package cannot depend on itself", path: ["dependsOn"] });
/** See {@link WorkPackageSchema}. */
export type WorkPackage = z.infer<typeof WorkPackageSchema>;

/** A gap in the spec the planner refused to guess at. */
export const PlanQuestionSchema = z.object({
  /** Short id, unique within the plan, for example `Q-1`. */
  id: z.string().regex(/^[A-Za-z0-9][\w.-]{0,31}$/),
  /** The question. */
  text: z.string().min(1),
  /** True when the plan cannot be approved until this is answered. */
  blocking: z.boolean(),
  /** The answer, once a human gives one. Null until then. */
  answer: z.string().min(1).nullable(),
});
/** See {@link PlanQuestionSchema}. */
export type PlanQuestion = z.infer<typeof PlanQuestionSchema>;

function planIssues(plan: { packages: { id: string; dependsOn: string[] }[]; questions: { id: string }[] }): string[] {
  const issues: string[] = [];
  const ids = plan.packages.map((p) => p.id);
  if (new Set(ids).size !== ids.length) issues.push("package ids must be unique within a plan");
  if (new Set(plan.questions.map((q) => q.id)).size !== plan.questions.length) issues.push("question ids must be unique");
  const known = new Set(ids);
  for (const p of plan.packages) {
    for (const d of p.dependsOn) if (!known.has(d)) issues.push(`${p.id} depends on ${d}, which is not in this plan`);
  }
  // Cycle check (depth-first, colouring).
  const state = new Map<string, 1 | 2>();
  const deps = new Map(plan.packages.map((p) => [p.id, p.dependsOn]));
  const visit = (id: string, trail: string[]): void => {
    if (state.get(id) === 2) return;
    if (state.get(id) === 1) {
      issues.push(`dependency cycle: ${[...trail, id].join(" -> ")}`);
      return;
    }
    state.set(id, 1);
    for (const d of deps.get(id) ?? []) if (deps.has(d)) visit(d, [...trail, id]);
    state.set(id, 2);
  };
  for (const id of ids) visit(id, []);
  return issues;
}

/**
 * One amendment to an approved plan (contracts-v3), recorded on the plan. The run's commander amends a plan to
 * re-plan a failed or stalled package and to add gap packages, inside the authority the human's plan approval gave.
 */
export const PlanAmendmentSchema = z.object({
  /** When it was applied. */
  at: TimestampSchema,
  /** Who amended it: a `system:` principal (the commander). */
  amendedBy: PrincipalSchema,
  /** Why, in a sentence or two. */
  reason: z.string().min(1),
  /** Packages added, as approved. */
  added: z.array(WpIdSchema),
  /** Packages superseded, and what replaces each. */
  superseded: z.array(z.object({ wpId: WpIdSchema, replacedBy: z.array(WpIdSchema) })),
  /** Dependencies added to packages that have not landed. */
  rewired: z.array(z.object({ wpId: WpIdSchema, addDependsOn: z.array(WpIdSchema) })),
});
/** See {@link PlanAmendmentSchema}. */
export type PlanAmendment = z.infer<typeof PlanAmendmentSchema>;

const planFields = {
  /** Identifies the spec this plan was made from, for example `season-2`. One plan per spec. */
  specId: z.string().regex(/^[a-z0-9][a-z0-9._-]{0,63}$/),
  /** Human-readable plan title, for example "Season 2". */
  title: z.string().min(1).max(120),
  /** Gaps in the spec, raised as questions rather than guessed. */
  questions: z.array(PlanQuestionSchema),
  /** Why packages are ordered the way they are, and which overlaps were pre-sequenced or left for runtime. */
  sequencingNotes: z.array(z.string().min(1)),
};

/** A plan as the planner submits it. The coordinator stores it as a draft. */
export const WorkPlanDraftSchema = z
  .object({ ...planFields, packages: z.array(WorkPackageDraftSchema).min(1) })
  .superRefine((plan, ctx) => {
    for (const message of planIssues(plan)) ctx.addIssue({ code: "custom", message, path: ["packages"] });
  });
/** See {@link WorkPlanDraftSchema}. */
export type WorkPlanDraft = z.infer<typeof WorkPlanDraftSchema>;

/**
 * The planner's output for one spec: work packages, open questions and sequencing notes.
 * Stays a draft (approvedBy null) until a human approves it.
 */
export const WorkPlanSchema = z
  .object({
    ...planFields,
    /** The packages, each with its tracked status. */
    packages: z.array(WorkPackageSchema).min(1),
    /** The human who approved the plan (`human:<handle>`). Null while it is a draft. */
    approvedBy: PrincipalSchema.nullable(),
    /** When it was approved. Null while it is a draft. */
    approvedAt: TimestampSchema.nullable(),
    /** When it was first submitted. */
    createdAt: TimestampSchema,
    /** Amendments since approval, oldest first (contracts-v3). Absent until the first one. */
    amendments: z.array(PlanAmendmentSchema).optional(),
  })
  .superRefine((plan, ctx) => {
    for (const message of planIssues(plan)) ctx.addIssue({ code: "custom", message, path: ["packages"] });
    if ((plan.approvedBy === null) !== (plan.approvedAt === null)) {
      ctx.addIssue({ code: "custom", message: "approvedBy and approvedAt are set together", path: ["approvedAt"] });
    }
  });
/** See {@link WorkPlanSchema}. */
export type WorkPlan = z.infer<typeof WorkPlanSchema>;
